import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class QuotaTriggerService {
  private readonly QUOTA_UPDATE_FLAGS_KEY = 'quotaUpdateFlags';

  constructor() {}

  /**
   * Mark that health declaration was updated for a company
   * @param companyId - The company ID
   */
  markHealthDeclarationUpdated(companyId: string): void {
    this.setUpdateFlag(companyId, 'healthDeclaration', true);
    console.log(`Health declaration update flag set for company: ${companyId}`);
  }

  /**
   * Mark that insurance class was updated for a company
   * @param companyId - The company ID
   */
  markInsuranceClassUpdated(companyId: string): void {
    this.setUpdateFlag(companyId, 'insuranceClass', true);
    console.log(`Insurance class update flag set for company: ${companyId}`);
  }

  /**
   * Check if quota should be refreshed for a company
   * @param companyId - The company ID
   * @returns true if quota should be refreshed
   */
  shouldRefreshQuota(companyId: string): boolean {
    const flags = this.getUpdateFlags(companyId);
    const shouldRefresh = flags.healthDeclaration || flags.insuranceClass;
    
    if (shouldRefresh) {
      console.log(`Quota refresh required for company ${companyId}:`, flags);
    }
    
    return shouldRefresh;
  }

  /**
   * Clear all update flags for a company (call after quota is refreshed)
   * @param companyId - The company ID
   */
  clearUpdateFlags(companyId: string): void {
    this.setUpdateFlag(companyId, 'healthDeclaration', false);
    this.setUpdateFlag(companyId, 'insuranceClass', false);
    console.log(`Update flags cleared for company: ${companyId}`);
  }

  /**
   * Get update flags for a company
   * @param companyId - The company ID
   * @returns Object with update flags
   */
  private getUpdateFlags(companyId: string): { healthDeclaration: boolean; insuranceClass: boolean } {
    try {
      const allFlags = JSON.parse(localStorage.getItem(this.QUOTA_UPDATE_FLAGS_KEY) || '{}');
      return allFlags[companyId] || { healthDeclaration: false, insuranceClass: false };
    } catch (error) {
      console.error('Error reading quota update flags:', error);
      return { healthDeclaration: false, insuranceClass: false };
    }
  }

  /**
   * Set a specific update flag for a company
   * @param companyId - The company ID
   * @param flagType - Type of flag to set
   * @param value - Flag value
   */
  private setUpdateFlag(companyId: string, flagType: 'healthDeclaration' | 'insuranceClass', value: boolean): void {
    try {
      const allFlags = JSON.parse(localStorage.getItem(this.QUOTA_UPDATE_FLAGS_KEY) || '{}');
      
      if (!allFlags[companyId]) {
        allFlags[companyId] = { healthDeclaration: false, insuranceClass: false };
      }
      
      allFlags[companyId][flagType] = value;
      
      localStorage.setItem(this.QUOTA_UPDATE_FLAGS_KEY, JSON.stringify(allFlags));
    } catch (error) {
      console.error('Error setting quota update flag:', error);
    }
  }

  /**
   * Clear all flags for all companies (for cleanup)
   */
  clearAllFlags(): void {
    localStorage.removeItem(this.QUOTA_UPDATE_FLAGS_KEY);
    console.log('All quota update flags cleared');
  }

  /**
   * Get debug information about current flags
   */
  getDebugInfo(): any {
    try {
      return JSON.parse(localStorage.getItem(this.QUOTA_UPDATE_FLAGS_KEY) || '{}');
    } catch (error) {
      return {};
    }
  }
}